import { useEffect, useRef, useState } from 'react'
import type * as Ably from 'ably'
import { createAblyClient, fetchAblyToken } from '@/lib/services/ably-service'
import { logger } from '@/lib/utils/logger'

export type ToolEventType =
    | 'tool:started'
    | 'tool:progress'
    | 'tool:completed'
    | 'tool:failed'

export interface ConsoleToolEvent {
    /** Ably message ID (unique per message) */
    id: string
    type: ToolEventType
    data: Record<string, unknown>
    timestamp: number
}

interface UseAblyConsoleOptions {
    agentId: string
    userId: string
    /** Set to false to disable the subscription (e.g. when IDs aren't ready) */
    enabled?: boolean
}

export interface UseAblyConsoleResult {
    /** Full ordered list of tool events received this session */
    events: ConsoleToolEvent[]
    /** Convenience: the most recent event, or null if none yet */
    latestEvent: ConsoleToolEvent | null
    /** Ably connection state string for debugging / UI indicators */
    connectionState: string
    /** Channel actually used for subscription (derived from capability) */
    subscribedChannel: string | null
}

const TOOL_EVENT_TYPES: ToolEventType[] = [
    'tool:started',
    'tool:progress',
    'tool:completed',
    'tool:failed',
]

function toToolsChannel(capabilityKey: string): string | null {
    const normalized = capabilityKey.trim()
    if (normalized.endsWith(':tools')) return normalized
    if (normalized.endsWith(':*')) return `${normalized.slice(0, -2)}:tools`
    return null
}

function resolveToolsChannelFromCapability(
    capability: string,
    agentId: string,
    preferredUserId: string
): string | null {
    try {
        const parsed = JSON.parse(capability) as Record<string, unknown>
        const keys = Object.keys(parsed)
        const scopedKeys = keys.filter((key) => key.startsWith(`glue:${agentId}:`))
        const candidateChannels = scopedKeys
            .map(toToolsChannel)
            .filter((value): value is string => !!value)

        const preferredChannel = `glue:${agentId}:${preferredUserId}:tools`
        if (candidateChannels.includes(preferredChannel)) {
            return preferredChannel
        }
        return candidateChannels[0] ?? null
    } catch {
        return null
    }
}

/**
 * Subscribes to the AI OS Console real-time tool events for a given agent+user pair.
 *
 * Channel: glue:{agentId}:{userId}:tools
 * Events:  tool:started | tool:progress | tool:completed | tool:failed
 *
 * The subscription is established when `enabled` is true and both IDs are
 * non-empty. It is torn down automatically on unmount or when dependencies change.
 */
export function useAblyConsole({
    agentId,
    userId,
    enabled = true,
}: UseAblyConsoleOptions): UseAblyConsoleResult {
    const [events, setEvents] = useState<ConsoleToolEvent[]>([])
    const [connectionState, setConnectionState] = useState<string>('initialized')
    const [subscribedChannel, setSubscribedChannel] = useState<string | null>(null)
    const clientRef = useRef<Ably.Realtime | null>(null)

    useEffect(() => {
        if (!enabled || !agentId || !userId) return

        let isDisposed = false
        let client: Ably.Realtime | null = null
        let channel: Ably.RealtimeChannel | null = null
        let resolvedChannelName: string | null = null

        const init = async () => {
            let initialToken
            try {
                initialToken = await fetchAblyToken(agentId)
            } catch (error) {
                logger.error('[useAblyConsole] Initial token fetch failed', error)
                return
            }

            if (isDisposed) return

            resolvedChannelName =
                resolveToolsChannelFromCapability(initialToken.capability, agentId, userId) ??
                `glue:${agentId}:${userId}:tools`
            setSubscribedChannel(resolvedChannelName)

            if (resolvedChannelName !== `glue:${agentId}:${userId}:tools`) {
                logger.warn('[useAblyConsole] Capability channel differs from requested userId', {
                    requestedChannel: `glue:${agentId}:${userId}:tools`,
                    resolvedChannel: resolvedChannelName,
                    capability: initialToken.capability,
                })
            }

            client = createAblyClient(agentId, initialToken)
            clientRef.current = client

            const handleConnectionStateChange = (stateChange: Ably.ConnectionStateChange) => {
                setConnectionState(stateChange.current)
                if (stateChange.current === 'failed') {
                    logger.error('[useAblyConsole] Connection failed', stateChange.reason)
                }
            }
            client.connection.on(handleConnectionStateChange)

            channel = client.channels.get(resolvedChannelName)

            const handleChannelStateChange = (stateChange: Ably.ChannelStateChange) => {
                if (stateChange.current === 'failed') {
                    logger.error('[useAblyConsole] Channel failed', {
                        channelName: resolvedChannelName,
                        reason: stateChange.reason,
                    })
                }
            }
            channel.on(handleChannelStateChange)

            const handleEvent = (msg: Ably.Message) => {
                const event: ConsoleToolEvent = {
                    id: msg.id ?? `${Date.now()}-${Math.random()}`,
                    type: msg.name as ToolEventType,
                    data: (msg.data ?? {}) as Record<string, unknown>,
                    timestamp: msg.timestamp ?? Date.now(),
                }
                logger.info('[useAblyConsole] Tool event received', {
                    channelName: resolvedChannelName,
                    type: event.type,
                    data: event.data,
                })
                setEvents((prev) => [...prev, event])
            }

            for (const eventType of TOOL_EVENT_TYPES) {
                void channel.subscribe(eventType, handleEvent).catch((error: unknown) => {
                    if (isDisposed) return
                    logger.error('[useAblyConsole] Subscribe failed', {
                        channelName: resolvedChannelName,
                        eventType,
                        error,
                    })
                })
            }

            logger.info('[useAblyConsole] Subscribed to channel', { channelName: resolvedChannelName })

            return () => {
                if (channel) {
                    channel.off(handleChannelStateChange)
                    for (const eventType of TOOL_EVENT_TYPES) {
                        try {
                            channel.unsubscribe(eventType, handleEvent)
                        } catch (error) {
                            logger.warn('[useAblyConsole] Unsubscribe failed', {
                                channelName: resolvedChannelName,
                                eventType,
                                error,
                            })
                        }
                    }
                }
                if (client) {
                    client.connection.off(handleConnectionStateChange)
                    client.connection.close()
                }
                clientRef.current = null
                logger.info('[useAblyConsole] Subscription cleaned up', { channelName: resolvedChannelName })
            }
        }

        let cleanup: (() => void) | void
        void init().then((teardown) => {
            cleanup = teardown
        })

        return () => {
            isDisposed = true
            setSubscribedChannel(null)
            cleanup?.()
        }
    }, [agentId, userId, enabled])

    const latestEvent = events.length > 0 ? events[events.length - 1] : null

    return { events, latestEvent, connectionState, subscribedChannel }
}
